import React from 'react';
import {
  Alert,
  Box,
  Button,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { Add, CloudUpload } from '@mui/icons-material';
import UploadReceiptDialog from '../../../components/audit/UploadReceiptDialog';
import AuditFilterBar from '../../../components/audit/AuditFilterBar';
import AuditsTable from '../../../components/audit/AuditsTable';
import AdminSurface from '../../../components/admin/AdminSurface';
import { useAuditPageLogic } from '../hooks/useAuditPageLogic';
import { adminButtonSx, adminButtonToneSx, adminCombineSx, adminPageHeaderSx } from '../../../styles/adminComponentStyles';
import 'react-datepicker/dist/react-datepicker.css';

const AuditPage: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { model, actions } = useAuditPageLogic();

  return (
    <Box>
      <Box sx={adminCombineSx(adminPageHeaderSx, { py: 0, mb: 2 })}>
        <Box>
          <Typography className="AdminPageHeader-title">
            OCR Dashboard
          </Typography>
          <Typography className="AdminPageHeader-meta">
            Monitor receipt OCR processing and review status
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
          <Button
            variant="contained"
            onClick={actions.onOpenUploadDialog}
            startIcon={<CloudUpload />}
            size="small"
            sx={adminCombineSx(adminButtonSx, adminButtonToneSx.primary, { minWidth: isMobile ? 'auto' : 160 })}
          >
            {isMobile ? 'Upload' : 'Upload Receipt'}
          </Button>
          <Button
            variant="outlined"
            onClick={actions.onNavigateToCreate}
            startIcon={<Add />}
            size="small"
            sx={adminCombineSx(adminButtonSx, adminButtonToneSx.secondary, { minWidth: isMobile ? 'auto' : 160 })}
          >
            {isMobile ? 'Create' : 'Create New Audit'}
          </Button>
        </Box>
      </Box>

      {model.error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={actions.onClearError}>
          {model.error}
        </Alert>
      )}

      <AdminSurface sx={{ border: '1px solid #e0e0e0', boxShadow: '0 2px 8px rgb(34 43 69 / 8%)', mb: 2, overflow: 'hidden', p: 0 }}>
        <Box sx={{ p: 2 }}>
        <AuditFilterBar
          stores={model.stores}
          storesLoading={model.storesLoading}
          pendingStoreFilter={model.pendingStoreFilter}
          pendingStatusFilter={model.pendingStatusFilter}
          pendingReportTypeFilter={model.pendingReportTypeFilter}
          pendingDateRange={model.pendingDateRange}
          pendingAgeDays={model.pendingAgeDays}
          allowedStatuses={model.allowedStatuses}
          setPendingStoreFilter={actions.setPendingStoreFilter}
          setPendingStatusFilter={actions.setPendingStatusFilter}
          setPendingReportTypeFilter={actions.setPendingReportTypeFilter}
          setPendingDateRange={actions.setPendingDateRange}
          setPendingAgeDays={actions.setPendingAgeDays}
          setFilters={actions.setFilters}
          updateURLParams={actions.updateURLParams}
          onClear={actions.onClearFilters}
        />
        </Box>

        <AuditsTable
        audits={model.sortedAudits}
        loading={model.loading}
        total={model.total}
        size={model.size}
        page={model.page}
        orderBy={model.orderBy}
        order={model.order}
        onRequestSort={actions.onRequestSort}
        onPageChange={actions.onPageChange}
        onRowsPerPageChange={actions.onRowsPerPageChange}
        onFixOCR={actions.onFixOCR}
        onRevertToReady={actions.onRevertToReady}
        onReprocess={actions.onReprocess}
          onNotesChanged={actions.onRefreshAudits}
        />
      </AdminSurface>

      <UploadReceiptDialog
        open={model.uploadDialogOpen}
        onClose={actions.onCloseUploadDialog}
        onSuccess={actions.onUploadSuccess}
        stores={model.stores}
        storesLoading={model.storesLoading}
      />
    </Box>
  );
};

export default AuditPage;
